import { makeObservable, observable, action, computed, flow } from "mobx";
import BaseDriveStore from "../Base/BaseDriveStore";
import { listFoldersByTenant } from "../../../../../services/folders";
import { listDocumentsByTenant, deleteDocumentByTenant } from "../../../../../services/document";

class ActiveFolderStore extends BaseDriveStore {
    activeFolderId = null;
    nameFolder = '';
    folderNamesMap = {}; // Map of UUID → name for breadcrumb
    viewMode = 'grid'; // 'card' | 'table'

    // Delete modal state
    isDeleteModalOpen = false;
    documentToDelete = null;
    isDeletingDocument = false;
    deleteError = null;

    constructor({ foldersStore, documentsStore, agentsStore, selectionStore }) {
        super({ foldersStore, documentsStore, agentsStore, selectionStore });

        makeObservable(this, {
            activeFolderId: observable,
            viewMode: observable,
            nameFolder: observable,
            folderNamesMap: observable,
            setActiveFolder: flow,
            loadFolderContents: flow,
            loadFolderNames: flow,
            currentFolder: computed,
            setActiveFolderName: action,
            setFolderName: action,
            // Delete document actions
            isDeleteModalOpen: observable,
            documentToDelete: observable,
            isDeletingDocument: observable,
            deleteError: observable,
            openDeleteModal: action,
            closeDeleteModal: action,
            deleteDocument: flow,
            removeDocumentFromList: action,
            extractErrorMessage: action
        });
    }

    // Delete Modal Actions
    openDeleteModal = (document) => {
        this.documentToDelete = document;
        this.isDeleteModalOpen = true;
    };

    closeDeleteModal = () => {
        this.isDeleteModalOpen = false;
        this.documentToDelete = null;
        this.deleteError = null;
    };

    *deleteDocument() {
        if (!this.documentToDelete) {
            return { success: false, error: 'No document selected for deletion' };
        }

        this.isDeletingDocument = true;
        this.deleteError = null;

        try {
            const documentUuid = this.documentToDelete.documentUuid || this.documentToDelete.document_uuid; 

            if (!documentUuid) {
                throw new Error('Document UUID not found');
            }

            const response = yield deleteDocumentByTenant({ document_uuid: documentUuid });

            const responseData = response?.data || response;
            if (responseData?.statusCode && responseData.statusCode >= 400) {
                const errorMsg = this.extractErrorMessage(responseData);
                throw new Error(errorMsg);
            }

            this.removeDocumentFromList(documentUuid);

            return { 
                success: true,
                title: 'Success',
                message: `Document "${this.documentToDelete.name}" deleted successfully`,
                variant: 'success'
            };
        } catch (error) {
            console.error('Error deleting document:', error);
            const errorMessage = this.extractErrorMessage(error.response?.data || error.message || 'Error deleting document');
            this.deleteError = errorMessage;
            return { 
                success: false, 
                error: errorMessage,
                title: 'Error',
                message: errorMessage,
                variant: 'error'
            };
        } finally {
            this.isDeletingDocument = false;
        }
    }

    extractErrorMessage = (errorData) => {
        if (typeof errorData === 'string') {
            return errorData;
        }

        if (errorData?.msgCode && Array.isArray(errorData.msgCode) && errorData.msgCode.length > 0) {
            const firstError = errorData.msgCode[0];
            return firstError.description || firstError.code || 'Error deleting document';
        }

        if (errorData?.message) {
            return errorData.message;
        }

        if (errorData?.error) {
            return errorData.error;
        }

        return 'Error deleting document';
    }

    removeDocumentFromList = (documentUuid) => {
        this.documentList = this.documentList.filter(
            doc => (doc.document_uuid || doc.documentUuid || doc.id) !== documentUuid
        );
    };

    // Duplicate Modal Actions
    openDuplicateModal = (item, type) => {
        this.itemToDuplicate = { ...item, type };
        this.isDuplicateModalOpen = true;
    };

    closeDuplicateModal = () => {
        this.isDuplicateModalOpen = false;
        this.itemToDuplicate = null;
    };

    *duplicateItem() {
        if (!this.itemToDuplicate) {
            console.warn('No item to duplicate');
            return { success: false };
        }

        try {
            if (this.itemToDuplicate.type === 'folder') {
                // Duplicate folder usando el endpoint específico
                const folderUuid = this.itemToDuplicate.folder_uuid;
                const duplicatedFolder = yield this.foldersStore.duplicateFolder(folderUuid);

                if (duplicatedFolder) {
                    // Agregar directamente a la lista de folders sin recargar todo
                    this.setFolderList([...this.folderList, duplicatedFolder]);
                    this.closeDuplicateModal();
                    return {
                        success: true,
                        data: duplicatedFolder,
                        title: 'Success',
                        message: `Folder "${this.itemToDuplicate.name}" duplicated successfully`,
                        variant: 'success'
                    };
                }
            } else if (this.itemToDuplicate.type === 'document') {
                // Duplicate document usando el endpoint específico
                const documentUuid = this.itemToDuplicate.document_uuid;
                const duplicatedDocument = yield this.documentsStore.duplicateDocument(documentUuid);

                if (duplicatedDocument) {
                    // Agregar directamente a la lista de documents sin recargar todo
                    this.setDocumentList([...this.documentList, duplicatedDocument]);
                    this.closeDuplicateModal();
                    return {
                        success: true,
                        data: duplicatedDocument,
                        title: 'Success',
                        message: `Document "${this.itemToDuplicate.name}" duplicated successfully`,
                        variant: 'success'
                    };
                }
            }

            return {
                success: false,
                message: 'Unable to duplicate item. Please try again.',
                variant: 'error'
            };
        } catch (error) {
            console.error('Error duplicating item:', error);
            return {
                success: false,
                message: error.message || 'An unexpected error occurred while duplicating the item',
                variant: 'error'
            };
        }
    }

    // Active Folder Actions
    *setActiveFolder(folderId, vaultUuid = null) {
        this.activeFolderId = folderId;
        if (folderId) {
            return yield this.loadFolderContents(folderId, vaultUuid);
        }
    };

    setActiveFolderName = (folderName) => {
        this.nameFolder = folderName;
    }

    // Save folder name by UUID
    setFolderName = (uuid, name) => {
        this.folderNamesMap = {
            ...this.folderNamesMap,
            [uuid]: name
        };
    }

    // Load folder names from URL hierarchy
    *loadFolderNames(uuidHierarchy, vaultUuid = null) {
        try {
            // For each UUID, fetch the folder that contains it as a child
            // Make requests in parallel for optimization
            const folderPromises = uuidHierarchy.map((uuid, index) => {
                // For the first folder (root), fetch without parent_folder_uuid
                if (index === 0) {
                    return listFoldersByTenant({ 
                        vault_uuid: vaultUuid,
                        parent_folder_uuid: null,
                        filters: {},
                        cursor: null
                    });
                }
                // For others, fetch using the parent (previous UUID in hierarchy)
                const parentUuid = uuidHierarchy[index - 1];
                return listFoldersByTenant({ 
                    vault_uuid: vaultUuid,
                    parent_folder_uuid: parentUuid,
                    filters: {},
                    cursor: null
                });
            });

            const responses = yield Promise.all(folderPromises);

            // Process each response and map UUID to name
            let allFoldersFound = true;
            responses.forEach((response, index) => {
                const uuid = uuidHierarchy[index];
                const folders = response?.data?.body || [];
                const folder = folders.find(f => f.folderUuid === uuid);
                
                if (folder) {
                    this.setFolderName(uuid, folder.name);
                } else {
                    // Folder not found in hierarchy
                    allFoldersFound = false;
                }
            });
            
            // Check if all folders were found
            if (!allFoldersFound) {
                return false; // Indicate folder not found
            }
            
            // Set the last folder as the current one
            const currentUuid = uuidHierarchy[uuidHierarchy.length - 1];
            const currentFolderName = this.folderNamesMap[currentUuid];
            if (currentFolderName) {
                this.setActiveFolderName(currentFolderName);
                return true; // Success
            } else {
                return false; // Current folder name not found
            }
        } catch (error) {
            console.error('Error loading folder names:', error);
            return false; // Error occurred
        }
    }

    *loadFolderContents(folderId, vaultUuid = null) {
        try {
            this.isLoading = true;
            this.error = null;

            const foldersBody = {
                parent_folder_uuid: folderId,
                vault_uuid: vaultUuid,
                page: 1,
                page_size: 20,
                order_by: ["updated_at"],
                order_direction: ["DESC"],
                filters: {},
                cursor: null
            };

            const documentsBody = {
                folder_uuid: folderId,
                vault_uuid: vaultUuid
            };

            // Make both calls in parallel
            const [foldersResponse, documentsResponse] = yield Promise.all([
                listFoldersByTenant(foldersBody),
                listDocumentsByTenant(documentsBody)
            ]);

            // Update lists with responses
            if (foldersResponse?.data?.body) {
                const folders = foldersResponse.data.body || [];
                
                // Filter folders with self-reference or same as current folder
                const filteredFolders = folders.filter(folder => {
                    // Exclude if folder is the same as the one we're viewing
                    if (folder.folderUuid === folderId) {
                        return false;
                    }
                    // Exclude if parentFolderUuid is the same as its own folderUuid (circular reference)
                    if (folder.parentFolderUuid === folder.folderUuid) {
                        return false;
                    }
                    return true;
                });
                
                this.setFolderList(filteredFolders);
            }

            if (documentsResponse?.data?.body) {
                this.setDocumentList(documentsResponse.data.body || []);
            }
            
            return true; // Success
        } catch (error) {
            this.error = error;
            console.error('Error loading folder contents:', error);
            return false; // Error occurred
        } finally {
            this.isLoading = false;
        }
    }

    get currentFolder() {
        return this.activeFolderId ? this.foldersStore.folders.find(f => f.id === this.activeFolderId) : null;
    }
}

export default ActiveFolderStore;